import React, { useState } from 'react';
import ContactModal from '../../../modal/ContactModal';
import SplitText from '../../../common/SplitText';
import { HiOutlineArrowSmRight } from "react-icons/hi";

const getInTouchBg = process.env.PUBLIC_URL + '/images/get-in-touch-bg.webp';

export default function Contact() {
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);

    return (
        <section id="contact" className="relative px-4 sm:px-8 lg:px-12 py-12 sm:py-20 overflow-hidden">
            
            {/* Image Placed Behind the Main Section Rectangle - Shifted to Right */}
            <div className="absolute inset-0 w-full h-full flex justify-center lg:justify-end items-center pointer-events-none z-0 overflow-hidden">
                <img 
                    src={getInTouchBg} 
                    alt="Rainbow Trucks Get In Touch Artwork Backdrop" 
                    className="w-full h-full max-w-[1600px] object-cover sm:object-contain opacity-60 lg:opacity-75 blur-[0.5px] transform scale-105 translate-x-8 sm:translate-x-16 lg:translate-x-28 xl:translate-x-40"
                />
            </div>

            {/* Ambient Background Glows */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-gradient-to-r from-purple-200/30 via-pink-200/20 to-teal-200/30 rounded-full blur-3xl pointer-events-none z-0" />

            {/* Main Section Rectangle Card with Reduced Opacity (Frosted Glass) */}
            <div className="max-w-[1240px] mx-auto bg-white/50 backdrop-blur-lg rounded-[32px] sm:rounded-[44px] lg:rounded-[52px] border border-white/60 shadow-[0_20px_70px_-15px_rgba(104,50,146,0.1)] p-6 sm:p-10 lg:p-14 xl:p-16 relative z-10 overflow-hidden text-center transition-all duration-300 hover:shadow-[0_25px_80px_-10px_rgba(104,50,146,0.15)]">
                
                {/* Subtle Inner Glows */}
                <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#7B2CBF]/5 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#73C8BD]/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10 flex flex-col items-center max-w-[900px] mx-auto">
                    
                    {/* Heading with Decorative Radiating Rays */}
                    <div className="relative inline-block w-fit mx-auto">
                        {/* Left Sparkles */}
                        <div className="absolute -left-6 -top-3 sm:-left-8 sm:-top-4 flex flex-col gap-1 pointer-events-none">
                            <span className="w-3.5 h-1 bg-[#FF4E99] rounded-full rotate-[-45deg] translate-x-1" />
                            <span className="w-4.5 h-1 bg-[#FFA834] rounded-full rotate-[-15deg]" />
                            <span className="w-3.5 h-1 bg-[#0099FF] rounded-full rotate-[25deg] translate-x-1" />
                        </div>

                        {/* Right Sparkles */}
                        <div className="absolute -right-6 -top-3 sm:-right-8 sm:-top-4 flex flex-col gap-1 pointer-events-none">
                            <span className="w-3.5 h-1 bg-[#34C759] rounded-full rotate-[45deg] -translate-x-1" />
                            <span className="w-4.5 h-1 bg-[#7B61FF] rounded-full rotate-[15deg]" />
                            <span className="w-3.5 h-1 bg-[#A880FF] rounded-full rotate-[-25deg] -translate-x-1" />
                        </div>

                        <SplitText 
                            text="Get In Touch"
                            tag="h2"
                            className="text-[28px] sm:text-[38px] lg:text-[46px] xl:text-[50px] font-extrabold text-[#181F38] tracking-tight leading-tight"
                            textAlign="center"
                        />
                    </div>

                    {/* Subtitle description */}
                    <p className="text-[#5A6275] text-[15px] sm:text-[16px] xl:text-[17px] font-normal leading-relaxed mt-4 max-w-[620px] mx-auto text-center">
                        We'd love to hear from you! Whether you have a question, partnership idea, or just want to say hello, our team is here to help.
                    </p>

                    {/* Rainbow Divider Bar */}
                    <div className="w-[140px] sm:w-[160px] h-[4px] rounded-full bg-gradient-to-r from-[#FF4E99] via-[#FFA834] via-[#34C759] via-[#0099FF] to-[#7B61FF] mt-4 mb-8 sm:mb-12 shadow-sm mx-auto" />
                </div>

                {/* 3 Contact Cards in Responsive Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 relative z-10 max-w-[1140px] mx-auto text-left">
                    
                    {/* Card 1: Address */}
                    <div className="bg-white/75 backdrop-blur-md rounded-[24px] p-6 sm:p-7 border border-white/80 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_35px_rgba(104,50,146,0.1)] hover:-translate-y-1 transition-all duration-300 flex flex-col group">
                        <div className="w-14 h-14 rounded-full bg-[#F3E8FF] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300 mb-5">
                            <svg className="w-7 h-7 text-[#7B2CBF]" viewBox="0 0 24 24" fill="currentColor">
                                <path fillRule="evenodd" d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <span className="text-[#7B2CBF] font-bold text-[18px] tracking-wide block mb-2">Address</span>
                        <a 
                            href="https://www.google.com/maps/search/?api=1&query=10th+Street,+Ram+Nagar,+Phulithivakkam,+Madipakkam,+Chennai,+Tamil+Nadu+600091"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#4B5563] hover:text-[#7B2CBF] text-[14px] sm:text-[14.5px] leading-relaxed transition-colors"
                        >
                            10th Street, Ram Nagar, Phulithivakkam, Madipakkam, Saidapet, Kanchipuram, Tamil Nadu, India, 600091
                        </a>
                    </div>

                    {/* Card 2: Phone */}
                    <div className="bg-white/75 backdrop-blur-md rounded-[24px] p-6 sm:p-7 border border-white/80 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_35px_rgba(34,197,94,0.12)] hover:-translate-y-1 transition-all duration-300 flex flex-col group">
                        <div className="w-14 h-14 rounded-full bg-[#DCFCE7] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300 mb-5">
                            <svg className="w-7 h-7 text-[#16A34A]" viewBox="0 0 24 24" fill="currentColor">
                                <path fillRule="evenodd" d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <span className="text-[#16A34A] font-bold text-[18px] tracking-wide block mb-2">Phone</span>
                        <a 
                            href="tel:+919150013114" 
                            className="text-[#181F38] hover:text-[#16A34A] font-bold text-[17px] sm:text-[18px] transition-colors block mt-1"
                        >
                            +91 9150013114
                        </a>
                    </div>

                    {/* Card 3: Email */}
                    <div className="bg-white/75 backdrop-blur-md rounded-[24px] p-6 sm:p-7 border border-white/80 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_35px_rgba(37,99,235,0.12)] hover:-translate-y-1 transition-all duration-300 flex flex-col group">
                        <div className="w-14 h-14 rounded-full bg-[#DBEAFE] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300 mb-5">
                            <svg className="w-7 h-7 text-[#2563EB]" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                                <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
                            </svg>
                        </div>
                        <span className="text-[#2563EB] font-bold text-[18px] tracking-wide block mb-2">Email</span>
                        <a 
                            href="mailto:hello@rainbowtrucks.com" 
                            className="text-[#181F38] hover:text-[#2563EB] font-bold text-[16px] sm:text-[17px] transition-colors break-all block mt-1"
                        >
                            hello@rainbowtrucks.com
                        </a>
                    </div>

                </div>

                {/* Bottom CTA Button to open ContactModal */}
                <div className="mt-10 sm:mt-12 text-center relative z-10">
                    <button
                        type="button"
                        onClick={() => setIsContactModalOpen(true)}
                        className="group inline-flex items-center gap-3 bg-[#73C8BD] hover:bg-[#5ebcae] active:scale-95 text-white font-bold text-[15px] sm:text-[16px] px-8 sm:px-10 py-4 rounded-full shadow-[0_6px_24px_rgba(115,200,189,0.45)] hover:shadow-[0_8px_30px_rgba(115,200,189,0.65)] transition-all duration-300"
                    >
                        <span>Apply For Franchise Discovery</span>
                        <HiOutlineArrowSmRight className="text-[20px] group-hover:translate-x-1.5 transition-transform duration-200" />
                    </button>
                </div>
            </div>

            <ContactModal 
                isOpen={isContactModalOpen} 
                onClose={() => setIsContactModalOpen(false)} 
            />
        </section>
    );
}
